import React, { useReducer, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'

import CheckboxTreeFormsy from './CheckboxTreeFormsy'
import {
  APIPermissionTypes,
  localizeIdFromAPIPermission,
  apiPermissions,
  viewMyNotification,
} from 'app/auth/accessControlService'
import { arrayAsHookDependencies } from 'libs/hook'
import { useIntl } from 'react-intl'

const SELECT_PERMISSIONS = 'SELECT_PERMISSIONS'

const permissionIdByKey = R.curry((key, permissions) =>
  R.compose(R.prop('id'), R.find(R.propEq('key', key)))(permissions),
)

const includeIds = (permissions, selectedIds) => {
  return R.any(
    R.includes(R.__, [
      permissionIdByKey(APIPermissionTypes.SUBSCRIBER_CREATE, permissions),
      permissionIdByKey(APIPermissionTypes.SUBSCRIBER_DELETE, permissions),
      permissionIdByKey(APIPermissionTypes.SUBSCRIBER_EDIT, permissions),
    ]),
    selectedIds,
  )
    ? [permissionIdByKey(APIPermissionTypes.SUBSCRIBER_VIEW, permissions)]
    : []
}

const selectPermissionReducer = (state, action) => {
  switch (action.type) {
    case SELECT_PERMISSIONS:
      const disabledIds = R.compose(concatViewMyNotification, includeIds)(
        action.permissions,
        action.permissionIds,
      )
      return {
        disabledIds,
        selectedIds: R.compose(
          R.uniq,
          R.concat(disabledIds),
        )(action.permissionIds),
      }
    default:
      return state
  }
}

const getPermissionIds = R.map(R.prop('id'))

export const localizeAPIPermissionName = R.curry(
  (formatMessage, apiPermission) =>
    R.set(
      R.lensProp('name'),
      R.compose(
        (id) => formatMessage({ id }),
        localizeIdFromAPIPermission,
        R.prop('key'),
      )(apiPermission),
      apiPermission,
    ),
)

const mapPermissionName = (formatMessage) =>
  R.map(localizeAPIPermissionName(formatMessage))

function getPermissionOptions(permissions, formatMessage) {
  return [
    {
      name: formatMessage({ id: 'rolesPermissions.select.section.subscriber' }),
      children: mapPermissionName(formatMessage)(
        permissions.filter(R.compose(R.includes('SUBSCRIBE'), R.prop('key'))),
      ),
    },
    {
      name: formatMessage({
        id: 'rolesPermissions.select.section.notification',
      }),
      children: mapPermissionName(formatMessage)(
        permissions.filter(
          R.compose(R.includes('NOTIFICATION'), R.prop('key')),
        ),
      ),
    },
  ]
}

function concatViewMyNotification(ids) {
  return R.includes(viewMyNotification.id, ids)
    ? ids
    : R.append(viewMyNotification.id, ids)
}

function PermissionTreeFormsy(props) {
  const { permissions, value } = props
  const initialPermissionIds = R.compose(
    concatViewMyNotification,
    getPermissionIds,
  )(value)
  const { formatMessage } = useIntl()

  const [selectedPermissionState, dispatchSelectPermissions] = useReducer(
    selectPermissionReducer,
    { selectedIds: [], disabledIds: [] },
  )

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!R.any(R.isEmpty, [permissions, initialPermissionIds])) {
      dispatchSelectPermissions({
        type: SELECT_PERMISSIONS,
        permissionIds: initialPermissionIds,
        permissions,
      })
    }
  }, [
    arrayAsHookDependencies(initialPermissionIds),
    arrayAsHookDependencies(permissions),
  ])
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <CheckboxTreeFormsy
      {...props}
      value={initialPermissionIds}
      currentValues={selectedPermissionState.selectedIds}
      onChange={(permissionIds) =>
        dispatchSelectPermissions({
          type: SELECT_PERMISSIONS,
          permissionIds,
          permissions,
        })
      }
      disabledValues={selectedPermissionState.disabledIds}
      multiple
      options={getPermissionOptions(apiPermissions, formatMessage)}
    />
  )
}

PermissionTreeFormsy.propTypes = {
  value: PropTypes.array.isRequired,
  permissions: PropTypes.array.isRequired,
}

export default React.memo(PermissionTreeFormsy)
